export const MAX_DISPLAY_POINTS = 2000;

export type TimestampRange = [number, number];
export type PointIndexRange = [number, number];

interface PointTimeIndexMetadata {
  retainedFrom: number | null;
  retainedTo: number | null;
  pointCount: number;
  pointTimes: number[];
}

const clamp = (value: number, minimum: number, maximum: number) => Math.min(Math.max(value, minimum), maximum);

export const getInitialPointIndexRange = (pointCount: number, displayLimit: number): PointIndexRange | null => {
  if (pointCount <= 0 || displayLimit <= 0) {
    return null;
  }

  const endIndex = pointCount - 1;
  return [Math.max(0, pointCount - displayLimit), endIndex];
};

export const isValidPointTimeIndex = ({
  retainedFrom,
  retainedTo,
  pointCount,
  pointTimes,
}: PointTimeIndexMetadata): boolean => {
  if (!Number.isSafeInteger(pointCount) || pointCount < 0 || !Array.isArray(pointTimes)) {
    return false;
  }
  if (pointTimes.length !== pointCount) {
    return false;
  }
  if (pointCount === 0) {
    return retainedFrom === null && retainedTo === null;
  }
  if (
    retainedFrom === null ||
    retainedTo === null ||
    !Number.isSafeInteger(retainedFrom) ||
    !Number.isSafeInteger(retainedTo) ||
    pointTimes[0] !== retainedFrom ||
    pointTimes[pointTimes.length - 1] !== retainedTo
  ) {
    return false;
  }

  return pointTimes.every(
    (pointTime, index) => Number.isSafeInteger(pointTime) && (index === 0 || pointTime >= pointTimes[index - 1]),
  );
};

export const getPointTimeRange = (pointTimes: number[], pointIndexRange: PointIndexRange): TimestampRange | null => {
  const [startIndex, endIndex] = pointIndexRange;
  if (
    !Number.isSafeInteger(startIndex) ||
    !Number.isSafeInteger(endIndex) ||
    startIndex < 0 ||
    endIndex < startIndex ||
    endIndex >= pointTimes.length
  ) {
    return null;
  }

  return [pointTimes[startIndex], pointTimes[endIndex]];
};

export const constrainPointIndexRange = (
  proposedRange: PointIndexRange,
  bounds: PointIndexRange,
  maxPointCount: number,
  activeThumb: number,
): PointIndexRange => {
  const [minimumIndex, maximumIndex] = bounds;
  const maximumSpan = Math.max(0, Math.floor(maxPointCount) - 1);
  const proposedStart = clamp(Math.round(proposedRange[0]), minimumIndex, maximumIndex);
  const proposedEnd = clamp(Math.round(proposedRange[1]), minimumIndex, maximumIndex);

  if (activeThumb === 0) {
    return [Math.max(proposedStart, proposedEnd - maximumSpan), proposedEnd];
  }

  return [proposedStart, Math.min(proposedEnd, proposedStart + maximumSpan)];
};

export const movePointIndexRange = (
  range: PointIndexRange,
  indexDelta: number,
  bounds: PointIndexRange,
): PointIndexRange => {
  const [minimumIndex, maximumIndex] = bounds;
  const span = range[1] - range[0];
  const requestedStart = range[0] + Math.round(indexDelta);
  const start = clamp(requestedStart, minimumIndex, Math.max(minimumIndex, maximumIndex - span));

  return [start, start + span];
};

export const limitMapPoints = <T>(points: T[], maxDisplayPoints = MAX_DISPLAY_POINTS): T[] => {
  if (maxDisplayPoints <= 0) {
    return [];
  }

  return points.length > maxDisplayPoints ? points.slice(points.length - maxDisplayPoints) : points;
};
